import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { User, Bell, Globe, Eye, EyeOff, Building2 } from 'lucide-react';
import { useOrg } from '../contexts/OrgContext';
import { organizationsApi, configApi } from '../lib/api';
import { useToast } from '../components/Toast';
import { loadPrefs, savePrefs } from '../lib/user-prefs';

const errText = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

// Placeholder rows while a section loads, so nothing shows empty or as a false "0" (V.53).
function Skeleton({ testid }) {
  return (
    <div data-testid={testid} aria-busy="true" className="animate-pulse space-y-2">
      <div className="h-4 bg-muted rounded w-3/4"></div>
      <div className="h-4 bg-muted rounded w-1/2"></div>
    </div>
  );
}

// Org name, your role, members, quota usage and (read-only) feature flags.
/** Owner: delete the organization (typed confirmation). Others: leave it. */
function OrgDangerZone({ org, role, onDone }) {
  const { showToast } = useToast();
  const [typed, setTyped] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);

  const run = async (call, doneMessage) => {
    setBusy(true); setError('');
    try {
      await call();
      showToast(doneMessage, 'success');
      onDone();
    } catch (err) {
      setError(errText(err, 'Request failed'));
      if (err?.response?.status === 409) setForce(true);
    } finally {
      setBusy(false);
    }
  };

  if (role === 'owner') {
    const matches = typed.trim() === org.name;
    return (
      <div className="border border-red-300 rounded-md p-4 space-y-2" data-testid="org-danger-zone">
        <h3 className="text-lg font-semibold text-red-700">Delete organization</h3>
        <p className="text-sm text-muted-foreground">
          Removes the organization and its memberships. This cannot be undone. Type the organization name
          (<span className="font-medium">{org.name}</span>) to confirm.
        </p>
        <input
          type="text" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="org-delete-confirm"
          className="w-full px-3 py-2 border border-input rounded-md text-sm" placeholder={org.name}
        />
        {force && (
          <label className="flex items-center gap-2 text-sm" data-testid="org-delete-force-row">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="org-delete-force" />
            Also delete all of its projects, datasets and files
          </label>
        )}
        {error && <p className="text-sm text-red-600" data-testid="org-delete-error">{error}</p>}
        <button
          type="button" disabled={!matches || busy} data-testid="org-delete-button"
          onClick={() => run(() => organizationsApi.remove(org.id, { force }), `Deleted ${org.name}`)}
          className="px-4 py-2 text-sm rounded-md bg-red-600 text-white disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete organization'}
        </button>
      </div>
    );
  }
  if (!role) return null;
  return (
    <div className="border border-border rounded-md p-4 space-y-2" data-testid="org-leave-zone">
      <h3 className="text-lg font-semibold">Leave organization</h3>
      <p className="text-sm text-muted-foreground">You will lose access to {org.name} and its projects.</p>
      {error && <p className="text-sm text-red-600" data-testid="org-leave-error">{error}</p>}
      {!confirmLeave ? (
        <button type="button" data-testid="org-leave-button" onClick={() => setConfirmLeave(true)}
          className="px-4 py-2 text-sm rounded-md border border-input">Leave organization</button>
      ) : (
        <div className="flex gap-2">
          <button type="button" disabled={busy} data-testid="org-leave-confirm"
            onClick={() => run(() => organizationsApi.leave(org.id), `Left ${org.name}`)}
            className="px-4 py-2 text-sm rounded-md bg-red-600 text-white disabled:opacity-50">
            {busy ? 'Leaving…' : 'Yes, leave'}
          </button>
          <button type="button" onClick={() => setConfirmLeave(false)} className="px-4 py-2 text-sm rounded-md border border-input">Cancel</button>
        </div>
      )}
    </div>
  );
}

export function OrganizationTab({ user }) {
  const { selectedOrg, selectedOrgId, refreshOrgs } = useOrg();
  const [members, setMembers] = useState(null);
  const [usage, setUsage] = useState(null);
  const [config, setConfig] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fail = (key) => (err) => { if (!cancelled) setErrors((e) => ({ ...e, [key]: errText(err, 'Request failed') })); };
    configApi.get().then((r) => { if (!cancelled) setConfig(r.data); }).catch(fail('config'));
    setMembers(null); setUsage(null); setErrors({});
    if (selectedOrgId) {
      organizationsApi.members(selectedOrgId).then((r) => { if (!cancelled) setMembers(r.data || []); }).catch(fail('members'));
      organizationsApi.usage(selectedOrgId).then((r) => { if (!cancelled) setUsage(r.data); }).catch(fail('usage'));
    }
    return () => { cancelled = true; };
  }, [selectedOrgId]);

  const myRole = (members || []).find((m) => m.user_id === user?.id)?.role;
  const features = Object.entries(config?.features || {});
  const Err = ({ k }) => (errors[k] ? <p className="text-sm text-red-600">{errors[k]}</p> : null);

  return (
    <div data-testid="org-tab" className="space-y-6">
      <h2 className="text-xl font-semibold">Organization</h2>
      {!selectedOrg ? (
        <p className="text-sm text-muted-foreground">No organization selected.</p>
      ) : (
        <>
          <div>
            <div className="text-lg font-medium" data-testid="org-name">{selectedOrg.name}</div>
            <div className="text-sm text-muted-foreground">
              Your role: <span data-testid="org-role" className="font-medium">{myRole || (members ? 'unknown' : <span data-testid="org-role-skeleton" className="inline-block h-3 w-12 bg-muted rounded animate-pulse align-middle" />)}</span>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Members</h3>
            <Err k="members" />
            {!members && !errors.members && <Skeleton testid="members-skeleton" />}
            {members && members.length === 0 && <p className="text-sm text-muted-foreground">No members.</p>}
            {members && members.length > 0 && (
              <table className="w-full text-sm" data-testid="org-members">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Name</th><th>Email</th><th>Role</th></tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id} className="border-t border-border">
                      <td className="py-1">{m.name || '-'}</td>
                      <td>{m.email || m.user_id}</td>
                      <td>{m.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Usage and quotas</h3>
            <Err k="usage" />
            {!usage && !errors.usage && <Skeleton testid="usage-skeleton" />}
            {usage && !usage.enabled && <p className="text-sm text-muted-foreground" data-testid="quotas-off">Quotas off</p>}
            {usage && usage.enabled && (
              <table className="w-full text-sm" data-testid="org-usage">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Bucket</th><th>Window</th><th>Used</th><th>Limit</th></tr></thead>
                <tbody>
                  {Object.entries(usage.budgets || {}).flatMap(([bucket, windows]) =>
                    Object.entries(windows).map(([win, limit]) => {
                      const used = usage.usage?.[bucket]?.[win];
                      const count = used && typeof used === 'object' ? used.used ?? used.count : used;
                      return (
                        <tr key={`${bucket}-${win}`} className="border-t border-border">
                          <td className="py-1">{bucket}</td><td>{win}</td><td>{count ?? '-'}</td><td>{limit || 'unlimited'}</td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            )}
          </div>
          {members && myRole && (
            <OrgDangerZone org={selectedOrg} role={myRole} onDone={() => { if (refreshOrgs) refreshOrgs(); }} />
          )}
        </>
      )}

      <div>
        <h3 className="text-lg font-semibold mb-2">Feature flags{config?.version ? ` (v${config.version})` : ''}</h3>
        <Err k="config" />
        {!config && !errors.config && <Skeleton testid="flags-skeleton" />}
        <ul className="text-sm space-y-1" data-testid="org-features">
          {features.map(([key, on]) => (
            <li key={key} className="flex gap-2"><span className="w-40">{key}</span><span className={on ? 'text-green-600' : 'text-muted-foreground'}>{on ? 'on' : 'off'}</span></li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-2">Read-only; set per deployment.</p>
      </div>
    </div>
  );
}

function Settings() {
  const { user, profile, updateProfile, updatePassword, signOut } = useAuth();
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const updatePrefs = (next) => {
    // demoMode is owned by the account menu / map ribbon; never overwrite it with a stale copy.
    const merged = { ...next, demoMode: loadPrefs().demoMode };
    setPrefs(merged);
    if (savePrefs(merged)) showToast('Preferences saved', 'success');
    else showToast('Could not save preferences in this browser', 'error');
  };
  const setNotif = (key, value) => updatePrefs({ ...prefs, notifications: { ...prefs.notifications, [key]: value } });
  const [activeTab, setActiveTab] = useState('profile');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
      setPhone(profile.phone || '');
    }
  }, [profile]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setProfileErr('');
      setProfileMsg('');
      setSavingProfile(true);
      await updateProfile({ first_name: firstName, last_name: lastName, phone });
      setProfileMsg('Profile updated.');
    } catch (err) {
      setProfileErr(err.response?.data?.detail || err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwErr('');
    setPwMsg('');
    if (newPassword.length < 6) {
      return setPwErr('Password must be at least 6 characters');
    }
    if (newPassword !== confirmPassword) {
      return setPwErr('Passwords do not match');
    }
    try {
      setSavingPw(true);
      await updatePassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setPwMsg('Password updated. You will be signed out — please sign in again with your new password.');
      // Give the user a moment to read the message, then sign them out.
      setTimeout(() => {
        signOut();
      }, 3000);
    } catch (err) {
      setPwErr(err.message || 'Failed to update password');
    } finally {
      setSavingPw(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: Globe },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />
        
        <h1 className="text-3xl font-bold mb-6">Settings</h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b border-border transition ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted text-muted-foreground'
                    }`}
                    data-testid={`settings-tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-card border border-border rounded-lg p-6">
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-xl font-semibold mb-4">Profile Information</h2>

                  {profileErr && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {profileErr}
                    </div>
                  )}
                  {profileMsg && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                      {profileMsg}
                    </div>
                  )}

                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">First name</label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="profile-first-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last name</label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="profile-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="profile-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="w-full px-3 py-2 border border-input rounded-md bg-muted text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">User ID</label>
                      <input
                        type="text"
                        value={user?.id || ''}
                        disabled
                        className="w-full px-3 py-2 border border-input rounded-md bg-muted text-muted-foreground monospace text-sm"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={savingProfile}
                      className="bg-primary text-primary-foreground py-2 px-4 rounded-md hover:opacity-90 transition disabled:opacity-50"
                      data-testid="save-profile-button"
                    >
                      {savingProfile ? 'Saving...' : 'Save changes'}
                    </button>
                  </form>

                  <hr className="my-6 border-border" />

                  <h3 className="text-lg font-semibold mb-3">Change password</h3>
                  {pwErr && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                      {pwErr}
                    </div>
                  )}
                  {pwMsg && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
                      {pwMsg}
                    </div>
                  )}
                  <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium mb-1">New password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          minLength={6}
                          className="w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                          data-testid="toggle-new-password"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Confirm new password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          minLength={6}
                          className="w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          data-testid="confirm-new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                          data-testid="toggle-confirm-new-password"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={savingPw}
                      className="bg-primary text-primary-foreground py-2 px-4 rounded-md hover:opacity-90 transition disabled:opacity-50"
                      data-testid="change-password-button"
                    >
                      {savingPw ? 'Updating...' : 'Update password'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'organization' && <OrganizationTab user={user} />}

              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-xl font-semibold mb-1">Notification Preferences</h2>
                  <p className="text-xs text-muted-foreground mb-4">Saved in this browser only; no emails are sent yet.</p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input type="checkbox" checked={prefs.notifications.qc} onChange={(e) => setNotif('qc', e.target.checked)} className="rounded" />
                      <span className="text-sm">Email notifications for QC completion</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" checked={prefs.notifications.upload} onChange={(e) => setNotif('upload', e.target.checked)} className="rounded" />
                      <span className="text-sm">Email notifications for upload completion</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input type="checkbox" checked={prefs.notifications.weekly} onChange={(e) => setNotif('weekly', e.target.checked)} className="rounded" />
                      <span className="text-sm">Weekly project summary emails</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div>
                  <h2 className="text-xl font-semibold mb-1">Application Preferences</h2>
                  <p className="text-xs text-muted-foreground mb-4">Saved in this browser.</p>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-3">Theme</label>
                      <ThemeSwitcher />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Default Map View</label>
                      <select
                        className="px-3 py-2 border border-input rounded-md"
                        value={prefs.defaultMapView}
                        onChange={(e) => updatePrefs({ ...prefs, defaultMapView: e.target.value })}
                      >
                        <option value="map">Map</option>
                        <option value="satellite">Satellite</option>
                        <option value="topo">Topo</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Settings;
